import * as v from "valibot";
import type { ApiSchema } from "../api/schema.ts";
import {
  AnswerOutSchema,
  EvaluationType,
  InfoOutcomeSchema,
  PassOutcomeSchema,
  ScoreOutcomeSchema,
  SectionSchema,
} from "../types/assessment.ts";
import { EvidenceOutSchema } from "../types/evidence.ts";
import { ImprovementActionDecision } from "../types/result.ts";
import { ReassessmentMessageOutSchema } from "../types/reassessmentMessage.ts";
import { ImprovementActionReassessmentOutSchema } from "../types/improvementActionReassessment.ts";

export const reassessmentStatus = {
  scheduled: "SCHEDULED",
  active: "ACTIVE",
  submitted: "SUBMITTED",
  accepted: "ACCEPTED",
  retired: "RETIRED",
  missing: "MISSING",
} as const;

export const ReassessmentStatusSchema = v.picklist(
  Object.values(reassessmentStatus),
);

export type ReassessmentStatus = v.InferOutput<typeof ReassessmentStatusSchema>;

const DateSchema = v.pipe(v.string(), v.isoDate());

// Days the supplier gets to respond once a reassessment activates. Stored on
// the row at schedule time so changing this never moves an existing deadline.
export const DEFAULT_RESPONSE_WINDOW_DAYS = 28;

export const reassessmentInterval = {
  twelveMonths: "TWELVE_MONTHS",
  custom: "CUSTOM",
} as const;

export const ReassessmentIntervalSchema = v.picklist(
  Object.values(reassessmentInterval),
);

export type ReassessmentInterval = v.InferOutput<
  typeof ReassessmentIntervalSchema
>;

export const ReassessmentSchema = v.object({
  contractId: v.string(),
  tenderPackageId: v.string(),
  urlId: v.string(),
  status: ReassessmentStatusSchema,
  resolvedDate: v.exactOptional(v.string()),
  interval: ReassessmentIntervalSchema,
  date: DateSchema,
  deadline: v.exactOptional(DateSchema),
  responseWindowDays: v.optional(v.number(), DEFAULT_RESPONSE_WINDOW_DAYS),
  collectionId: v.string(),
  assessmentId: v.string(),
  collectionName: v.string(),
  assessmentName: v.string(),
  supplierOrganisationId: v.string(),
  supplierWorkspaceId: v.string(),
  useLatestVersion: v.boolean(),
  authorId: v.string(),
  createdDate: v.string(),
  lastUpdated: v.string(),
});

export type Reassessment = v.InferOutput<typeof ReassessmentSchema>;

const OutcomeSchema = v.union([
  InfoOutcomeSchema,
  PassOutcomeSchema,
  ScoreOutcomeSchema,
]);

// One question's improvement action: the submitted version under review and
// the submitted versions before it, oldest first. Drafts stay with the supplier
export const ImprovementActionVersionsSchema = v.object({
  questionId: v.string(),
  current: ImprovementActionReassessmentOutSchema,
  history: v.array(ImprovementActionReassessmentOutSchema),
});

export type ImprovementActionVersions = v.InferOutput<
  typeof ImprovementActionVersionsSchema
>;

// The supplier's one submission plus every version of each improvement
// action written against it
export const ReassessmentResponseSchema = v.object({
  urlId: v.string(),
  evaluationType: v.picklist(Object.values(EvaluationType)),
  sections: v.array(SectionSchema),
  outcome: OutcomeSchema,
  resolvedDate: v.exactOptional(v.string()),
  score: v.exactOptional(v.number()),
  organisationName: v.string(),
  authorId: v.string(),
  createdDate: v.string(),
  answers: v.array(AnswerOutSchema),
  failedAnswers: v.exactOptional(v.array(AnswerOutSchema)),
  improvementActions: v.array(ImprovementActionVersionsSchema),
  evidence: v.array(EvidenceOutSchema),
});

export type ReassessmentResponse = v.InferOutput<
  typeof ReassessmentResponseSchema
>;

const ScheduleBodyEntries = {
  contractId: v.string(),
  assessments: v.pipe(
    v.array(v.object({ collectionId: v.string(), assessmentId: v.string() })),
    v.minLength(1),
  ),
  suppliers: v.pipe(
    v.array(v.object({ organisationId: v.string(), workspaceId: v.string() })),
    v.minLength(1),
  ),
  useLatestVersion: v.optional(v.boolean(), true),
};

const ScheduleReassessmentBodySchema = v.variant("interval", [
  v.object({
    ...ScheduleBodyEntries,
    interval: v.literal(reassessmentInterval.twelveMonths),
    startDate: DateSchema,
  }),
  v.object({
    ...ScheduleBodyEntries,
    interval: v.literal(reassessmentInterval.custom),
    dates: v.pipe(v.array(DateSchema), v.maxLength(100)),
  }),
]);

// One assessment on one date, across every supplier
const OccurrenceTargetSchema = v.object({
  contractId: v.string(),
  collectionId: v.string(),
  assessmentId: v.string(),
  date: DateSchema,
});

const CancelReassessmentBodySchema = OccurrenceTargetSchema;

const RescheduleReassessmentBodySchema = v.object({
  ...OccurrenceTargetSchema.entries,
  newDate: DateSchema,
});

export const MESSAGE_MAX_LENGTH = 200;

const MessageTextSchema = v.pipe(
  v.string(),
  v.trim(),
  v.minLength(1),
  v.maxLength(MESSAGE_MAX_LENGTH),
);

const DecideImprovementActionBodySchema = v.object({
  contractId: v.string(),
  urlId: v.string(),
  questionId: v.string(),
  version: v.pipe(v.number(), v.integer(), v.minValue(1)),
  decision: v.enum(ImprovementActionDecision),
  reason: v.pipe(v.string(), v.trim(), v.minLength(1)),
});

const PostMessageBodySchema = v.object({
  contractId: v.string(),
  urlId: v.string(),
  questionId: v.string(),
  text: MessageTextSchema,
});

const EditMessageBodySchema = v.object({
  contractId: v.string(),
  urlId: v.string(),
  questionId: v.string(),
  messageId: v.string(),
  text: MessageTextSchema,
});

const DeleteMessageBodySchema = v.object({
  contractId: v.string(),
  urlId: v.string(),
  questionId: v.string(),
  messageId: v.string(),
});

export const reassessmentsApi = {
  schedule: {
    method: "POST",
    path: "/reassessment",
    description:
      "Resolves the occurrence dates — expanded from startDate for repeating " +
      "and ad-hoc intervals, taken verbatim for a custom list — and writes " +
      "one SCHEDULED row per assessment × date × supplier. A date still " +
      "SCHEDULED for a supplier is replaced, one already under way is left " +
      "alone, and a SCHEDULED date the body no longer carries for those " +
      "assessments and suppliers is removed — the body is the source of " +
      "truth for what is scheduled, so an empty custom list clears it. " +
      "Occurrences dated today or earlier are " +
      "activated " +
      "immediately rather than waiting for the overnight cron. Validated " +
      "against the contract copy: caller is the buyer, dates fall within " +
      "the term, assessments belong to the contract's package.",
    context: "workspace",
    input: ScheduleReassessmentBodySchema,
    output: v.object({ data: v.array(ReassessmentSchema) }),
  },
  list: {
    method: "GET",
    path: "/reassessment/contract/{contractId}",
    description:
      "Reassessment occurrences on the contract. The buyer sees every " +
      "occurrence; a supplier party sees only its own ACTIVE, SUBMITTED " +
      "and ACCEPTED occurrences.",
    context: "workspace",
    output: v.object({ data: v.array(ReassessmentSchema) }),
  },
  listByAssessment: {
    method: "GET",
    path: "/reassessment/contract/{contractId}/collection/{collectionId}/assessment/{assessmentId}",
    description:
      "One assessment's reassessment occurrences on the contract, every " +
      "date and supplier, oldest first. Same visibility rules as list.",
    context: "workspace",
    output: v.object({ data: v.array(ReassessmentSchema) }),
  },
  response: {
    method: "GET",
    path: "/reassessment/contract/{contractId}/response/{urlId}",
    description:
      "The supplier's one submitted response for one occurrence: its " +
      "answers, outcome and evidence, plus every submitted version of each " +
      "improvement action written against it; drafts never appear. Visible " +
      "to the contract's buyer and to the supplier that submitted it.",
    context: "workspace",
    output: v.object({ data: ReassessmentResponseSchema }),
  },
  decideImprovementAction: {
    method: "POST",
    path: "/reassessment/decision",
    description:
      "The buyer's verdict on the current version of one improvement " +
      "action, with a mandatory reason saved on that version and shown to " +
      "the supplier. A rejected action stays open for the supplier to " +
      "revise as a new version; an accepted one is locked until the buyer " +
      "changes their decision. The occurrence is ACCEPTED while every " +
      "failed answer's current action is accepted and reopens as " +
      "SUBMITTED otherwise. Buyer only; a version the supplier has since " +
      "revised is refused.",
    context: "workspace",
    input: DecideImprovementActionBodySchema,
    output: v.object({ data: ReassessmentSchema }),
  },
  reschedule: {
    method: "PATCH",
    path: "/reassessment/schedule",
    description:
      "Moves one assessment's SCHEDULED occurrences on a date to a new date " +
      "within the contract term, for every supplier, as fresh rows. Active " +
      "and later occurrences are never touched. A new date that is already due " +
      "activates immediately. Buyer only.",
    context: "workspace",
    input: RescheduleReassessmentBodySchema,
    output: v.object({ data: v.array(ReassessmentSchema) }),
  },
  cancel: {
    method: "DELETE",
    path: "/reassessment/schedule",
    description:
      "Removes one assessment's occurrences on a date, for every supplier. " +
      "SCHEDULED occurrences are deleted outright (nothing " +
      "was sent to a supplier yet); ACTIVE, SUBMITTED or resolved ones are " +
      "skipped and counted. Buyer only.",
    context: "workspace",
    input: CancelReassessmentBodySchema,
    output: v.object({
      data: v.object({
        deleted: v.number(),
        skipped: v.number(),
      }),
    }),
  },
  listMessages: {
    method: "GET",
    path: "/reassessment/contract/{contractId}/messages/{urlId}",
    description:
      "Every discussion message on the occurrence, across all questions, " +
      "in thread order within each question. Visible to the contract's " +
      "buyer and to the responding supplier.",
    context: "workspace",
    output: v.object({ data: v.array(ReassessmentMessageOutSchema) }),
  },
  postMessage: {
    method: "POST",
    path: "/reassessment/message",
    description:
      "Posts a message on one question of the occurrence. Either party can " +
      "post; the author's name and party are stamped on the message at " +
      "write time.",
    context: "workspace",
    input: PostMessageBodySchema,
    output: v.object({ data: ReassessmentMessageOutSchema }),
  },
  editMessage: {
    method: "PATCH",
    path: "/reassessment/message",
    description:
      "Replaces a message's text, author only. The previous text and the " +
      "time it was written are pushed onto the message's version history; " +
      "the edit is rejected once that history would exceed the item size " +
      "limit.",
    context: "workspace",
    input: EditMessageBodySchema,
    output: v.object({ data: ReassessmentMessageOutSchema }),
  },
  deleteMessage: {
    method: "DELETE",
    path: "/reassessment/message",
    description:
      "Tombstones a message, author only: blanks the text and version " +
      "history and marks it deleted, leaving the breadcrumb in the thread. " +
      "Idempotent.",
    context: "workspace",
    input: DeleteMessageBodySchema,
    output: v.object({ data: ReassessmentMessageOutSchema }),
  },
} satisfies ApiSchema;
